import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Breadcrumb, Button, PageHeader, Spinner, StatCard } from '@jordan/ui';
import { AppChrome } from '../components/AppChrome';
import { useMe } from '../auth/MeProvider';
import { apiGet } from '../lib/api';

interface BookingDto {
  id: string;
  eventTitle: string;
  inviteeName: string;
  inviteeEmail: string;
  startsAt: string;
  timezone: string;
  status: 'CONFIRMED' | 'CANCELLED';
}

// TODO(config): number of recent bookings shown on the dashboard — move to the config service.
const RECENT_BOOKINGS_LIMIT = 6;

const fmtWhen = (iso: string, tz: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short', timeZone: tz });

export function DashboardPage() {
  const me = useMe();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<BookingDto[] | null>(null);
  const [bookingsError, setBookingsError] = useState(false);

  useEffect(() => {
    apiGet<BookingDto[]>('/api/openslot/bookings')
      .then((list) => setBookings(list.slice(0, RECENT_BOOKINGS_LIMIT)))
      .catch(() => setBookingsError(true));
  }, []);

  return (
    <AppChrome>
      <PageHeader
        title={
          <>
            Home - <span className="fw-normal">Dashboard</span>
          </>
        }
        breadcrumb={
          <Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Home', href: '/' }, { label: 'Dashboard' }]} />
        }
      />

      <div className="content">
        <div className="row g-3 mb-3">
          <div className="col-sm-6 col-xl-3">
            <StatCard value="38,289" label="Total visits" icon="ph-users" color="primary" trend={{ direction: 'up', value: '+16%' }} />
          </div>
          <div className="col-sm-6 col-xl-3">
            <StatCard value="$48.2k" label="Revenue" icon="ph-currency-dollar" color="success" trend={{ direction: 'up', value: '+8%' }} />
          </div>
          <div className="col-sm-6 col-xl-3">
            <StatCard value="2,458" label="Orders" icon="ph-shopping-cart" color="info" trend={{ direction: 'down', value: '-5%' }} />
          </div>
          <div className="col-sm-6 col-xl-3">
            <StatCard value="1,120" label="Open tickets" icon="ph-lifebuoy" color="warning" />
          </div>
        </div>

        <div className="row g-3">
          <div className="col-xl-4">
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">Your profile</h5>
              </div>
              <div className="card-body">
                {me === undefined && <p className="text-muted mb-0">Loading…</p>}
                {me === null && <div className="alert alert-warning mb-0">Could not load your profile.</div>}
                {me && (
                  <dl className="row mb-0">
                    <dt className="col-sm-4">Email</dt>
                    <dd className="col-sm-8">{me.email}</dd>
                    <dt className="col-sm-4">Name</dt>
                    <dd className="col-sm-8">{me.displayName ?? '—'}</dd>
                    <dt className="col-sm-4">Role</dt>
                    <dd className="col-sm-8">
                      <span className="badge bg-primary bg-opacity-10 text-primary">{me.role}</span>
                    </dd>
                  </dl>
                )}
              </div>
            </div>
          </div>

          <div className="col-xl-8">
            <div className="card h-100">
              <div className="card-header d-flex align-items-center">
                <h5 className="card-title mb-0">Recent bookings</h5>
                <Button variant="light" size="sm" className="ms-auto" onClick={() => navigate('/openslot/bookings')}>
                  View all
                </Button>
              </div>

              {bookingsError ? (
                <div className="card-body"><div className="alert alert-warning mb-0">Could not load recent bookings.</div></div>
              ) : !bookings ? (
                <div className="card-body"><Spinner size="sm" /></div>
              ) : bookings.length === 0 ? (
                <div className="card-body text-muted">No bookings yet. Share an Open Slot link to start receiving them.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table text-nowrap">
                    <thead>
                      <tr>
                        <th>Event</th>
                        <th>Invitee</th>
                        <th>When</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map((b) => (
                        <tr key={b.id}>
                          <td className="fw-semibold">{b.eventTitle}</td>
                          <td>
                            <div>{b.inviteeName}</div>
                            <div className="text-muted fs-sm">{b.inviteeEmail}</div>
                          </td>
                          <td className="text-muted">{fmtWhen(b.startsAt, b.timezone)}</td>
                          <td>
                            <Badge variant={b.status === 'CONFIRMED' ? 'success' : 'secondary'} soft>
                              {b.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppChrome>
  );
}
